import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCategoryDescriptions, submitFeedback } from '../services/api';
import { getCategoryIcon } from '../components/CategoryIcons';
import { useDocumentTitle } from '../utils/useDocumentTitle';
// import '../styles/About.css';

function About() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackData, setFeedbackData] = useState({
    name: '',
    email: '',
    feedbackType: 'suggestion',
    message: ''
  });

  // Set document title
  useDocumentTitle('About - RealSoft Newsletter');

  useEffect(() => {
    const fetchCategoryDescriptions = async () => {
      try {
        setLoading(true);
        const response = await getCategoryDescriptions();
        if (response.success) {
          setCategories(response.data);
        } else {
          throw new Error('Failed to load category descriptions');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading category descriptions:', err);
        setError('Failed to load category information. Please try again later.');
        setLoading(false);
      }
    };

    fetchCategoryDescriptions();
  }, []);

  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);
    setFeedbackError(null);

    try {
      const response = await submitFeedback(feedbackData);
      if (response && response.success) {
        setFeedbackSubmitted(true);
        // Reset form
        setFeedbackData({
          name: '',
          email: '',
          feedbackType: 'suggestion',
          message: ''
        });
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setFeedbackError(err.message || 'An error occurred. Please try again later.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <div>
      <Header />
      
      <main className="container">
        <section className="section">
          <h1>About RealSoft Newsletter</h1>
          
          <div className="about-content">
            <div className="about-description p-6 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mb-6">
              <p className="lead-text text-lg font-medium text-gray-800 mb-4">
                RealSoft Newsletter is a weekly technology newsletter focused on 
                providing valuable insights, trend analysis, and practical knowledge 
                for software developers, IT professionals, and technology enthusiasts.
              </p>
              
              <p className="text-gray-600">
                Our mission is to cut through the noise and deliver concise, actionable 
                content that helps you stay informed about what truly matters in the 
                rapidly evolving tech landscape.
              </p>
              
              <div className="about-categories">
                <h2>What We Cover</h2>
                {loading ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading category information...</p>
                  </div>
                ) : error ? (
                  <div className="error-message">
                    <p>{error}</p>
                  </div>
                ) : (
                  <div className="categories-grid">
                    {categories.map(category => (
                      <div className="category-card" key={category._id}>
                        <div className="category-icon">
                          {getCategoryIcon(category.name)}
                        </div>
                        <h3>{category.name}</h3>
                        <p>{category.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="section feedback-section">
          <h2>Share Your Feedback</h2>
          <p className="subtitle mb-6">
            We value your input! Please share your thoughts, suggestions, or report any issues you've encountered.
          </p>
          
          {feedbackSubmitted ? (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 shadow-sm">
              <h3 className="text-green-800 font-medium text-lg mb-2">Thank You for Your Feedback!</h3>
              <p className="text-green-700">
                We appreciate you taking the time to share your thoughts with us. 
                Your feedback helps us improve our newsletter and provide better content.
              </p>
              <button 
                onClick={() => setFeedbackSubmitted(false)}
                className="mt-4 px-5 py-2 bg-green-600 text-white font-medium rounded-lg shadow hover:bg-green-700 transition-colors"
              >
                Submit Another Response
              </button>
            </div>
          ) : (
            <div className="bg-white border-2 border-gray-400 rounded-lg shadow-sm p-6">
              {feedbackError && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
                  <p className="text-red-700">{feedbackError}</p>
                </div>
              )}
              
              <form onSubmit={handleFeedbackSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="form-group">
                    <label htmlFor="name" className="text-gray-700 font-medium">Your Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={feedbackData.name}
                      onChange={handleFeedbackChange}
                      className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 mt-1"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email" className="text-gray-700 font-medium">Your Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={feedbackData.email}
                      onChange={handleFeedbackChange}
                      className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 mt-1"
                    />
                  </div>
                </div>
                
                <div className="form-group mb-6">
                  <label htmlFor="feedbackType" className="text-gray-700 font-medium">Feedback Type *</label>
                  <div className="relative mt-1">
                    <select
                      id="feedbackType"
                      name="feedbackType"
                      required
                      value={feedbackData.feedbackType}
                      onChange={handleFeedbackChange}
                      className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white pl-4 pr-10 py-2 appearance-none"
                    >
                      <option value="suggestion">Suggestion</option>
                      <option value="content-request">Content Request</option>
                      <option value="bug-report">Bug Report</option>
                      <option value="compliment">Compliment</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="form-group mb-6">
                  <label htmlFor="message" className="text-gray-700 font-medium">Your Feedback *</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={feedbackData.message}
                    onChange={handleFeedbackChange}
                    className="w-full rounded-lg border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 mt-1"
                    placeholder="Please share your thoughts, suggestions, or report an issue..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center relative"
                  disabled={feedbackLoading}
                >
                  {feedbackLoading ? (
                    <>
                      <span className="inline-block w-5 h-5 mr-3 relative">
                        <span className="absolute inset-0 animate-ping rounded-full bg-white opacity-75"></span>
                        <span className="relative block w-5 h-5 rounded-full bg-white"></span>
                      </span>
                      Submitting...
                    </>
                  ) : 'Submit Feedback'}
                </button>
              </form>
            </div>
          )}
        </section>
        
        <section className="section team-section">
          <h2>Our Team</h2>
          <div className="team-grid">
            <div className="team-member">
              <div className="team-avatar">
                <div className="avatar-placeholder">TA</div>
              </div>
              <h3>Talha Alshafeey</h3>
              <p className="team-role">Figuring Things Out</p>
              <p className="team-bio">
                Talha is trying to sleep better every day.
              </p>
            </div>
            
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}

export default About; 